import { FC } from 'react';

import { Prospect } from 'crono-fe-common/types/prospect';

import { format } from 'date-fns';
import { Task } from 'crono-fe-common/types/task';
import { getProspectName } from 'crono-fe-common/utils';
import { LogLinkedin } from 'crono-fe-common/types/logLinkedin';
import { cn } from 'utils/style-utils';

interface LinkedinModalProps {
  prospect: Prospect | null;
  activity?: Task;
  logLinkedin?: LogLinkedin;
}

const renderUserFrom = (logLinkedin?: LogLinkedin, activity?: Task) => {
  if (!logLinkedin && !activity) return 'Unknown user';

  const from = logLinkedin?.owner ?? activity?.user;
  if (from) return from;

  return activity?.userId || logLinkedin?.userId
    ? 'Inactive user'
    : 'Unknown user';
};

interface MessageBlockProps {
  from: string;
  to?: string;
  dateStr?: string | null;
  content?: string;
  isReply?: boolean;
}

const MessageBlock: FC<MessageBlockProps> = ({
  from,
  to,
  dateStr,
  content,
  isReply = false,
}) => (
  <div
    className={cn(
      'flex w-full mb-2.5 min-h-10 justify-center flex-col',
      isReply && 'border-t border-neutral-300 mt-4 pt-4',
    )}
  >
    <div className="flex cursor-pointer justify-between">
      <div className="ml-2.5 flex flex-col">
        <span className="text-sm font-medium">from: {from}</span>
        {to && <span className="text-xs font-normal">to: {to}</span>}
      </div>
      {dateStr && (
        <span className="text-xs font-medium mr-5 text-gray-700">
          {format(new Date(dateStr), 'MMMM d, yyyy h:mm a')}
        </span>
      )}
    </div>
    <div className="flex w-full mt-5 items-start whitespace-pre-line justify-between flex-col ml-7.5 border-l-2 border-l-gray-300 pl-2.5">
      {content}
    </div>
  </div>
);

const LinkedinModal: FC<LinkedinModalProps> = ({
  prospect,
  activity,
  logLinkedin: _logLinkedin,
}) => {
  const logLinkedinToUse = activity?.linkedinLog ?? _logLinkedin;

  return (
    <div className="z-6000 bg-white size-full h-full p-0">
      <div className="flex flex-col bg-white rounded-[20px] pt-5 pr-10 pb-2.5 pl-0 mb-2.5 justify-start items-start">
        <div className="flex justify-between items-center">
          <p className="mb-0.5 mt-0 font-semibold text-4xl">
            {activity?.linkedinLog?.subject ?? activity?.subject ?? ''}
          </p>
        </div>
      </div>
      <div className="max-h-[calc(100%-100px)] overflow-y-auto bg-white rounded-b-[20px] pt-5 pr-5 pb-5 pl-0">
        {prospect && (activity?.description || logLinkedinToUse?.content) && (
          <MessageBlock
            from={renderUserFrom(logLinkedinToUse, activity)}
            to={getProspectName(prospect)}
            dateStr={activity?.createdDate || logLinkedinToUse?.eventDatetime}
            content={logLinkedinToUse?.content ?? activity?.description}
          />
        )}
        {prospect && logLinkedinToUse?.replyLinkedin && (
          <MessageBlock
            from={getProspectName(prospect)}
            dateStr={logLinkedinToUse.replyLinkedin.eventDatetime}
            content={logLinkedinToUse.replyLinkedin.content}
            isReply
          />
        )}
      </div>
    </div>
  );
};

export default LinkedinModal;
